import { Head } from 'vite-react-ssg';
import { DE_TO_EN, EN_TO_DE } from '../utils/i18nRoutes';

const SITE_URL = 'https://optiai.at';
const DEFAULT_OG_IMAGE = `${SITE_URL}/optiai-logo.png`;

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': `${SITE_URL}/#organization`,
  name: 'OptiAI Consulting',
  url: SITE_URL,
  logo: `${SITE_URL}/optiai-logo.png`,
  image: `${SITE_URL}/optiai-logo.png`,
  email: 'office@optiai.at',
  telephone: '+43-660-521-9262',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Moselgasse 26',
    postalCode: '1100',
    addressLocality: 'Wien',
    addressCountry: 'AT',
  },
  areaServed: [
    { '@type': 'Country', name: 'Austria' },
    { '@type': 'Country', name: 'Germany' },
    { '@type': 'Country', name: 'Switzerland' },
  ],
  sameAs: ['https://maps.google.com/?q=Moselgasse+26+Wien+1100'],
};

interface SeoProps {
  title: string;
  description: string;
  canonicalPath: string;
  ogImage?: string;
  jsonLd?: object | object[];
  noindex?: boolean;
}

export default function Seo({
  title,
  description,
  canonicalPath,
  ogImage = DEFAULT_OG_IMAGE,
  jsonLd,
  noindex = false,
}: SeoProps) {
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const pageJsonLd = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const jsonLdArray = [organizationSchema, ...pageJsonLd];

  const isEn = canonicalPath.startsWith('/en/') || canonicalPath === '/en';
  const ogLocale = isEn ? 'en_US' : 'de_AT';
  const htmlLang = isEn ? 'en' : 'de-AT';

  const dePath = isEn ? (EN_TO_DE[canonicalPath] || '/') : canonicalPath;
  const enPath = isEn ? canonicalPath : (DE_TO_EN[canonicalPath] || '/en/');

  const deUrl = `${SITE_URL}${dePath}`;
  const enUrl = `${SITE_URL}${enPath}`;

  return (
    <Head>
      <html lang={htmlLang} />

      <meta name="google-site-verification" content="xngD1_b4H1X01BQFJ0XwhMd0QViDANJ-YByDla4AF9Q" />

      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <link rel="alternate" hrefLang="de-AT" href={deUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={deUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="OptiAI" />
      <meta property="og:locale" content={ogLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdArray.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Head>
  );
}
